import { IonApp, IonContent, setupIonicReact } from '@ionic/react';

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import i18next from 'i18next';
import _ from 'lodash';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { AccessDenied, Modules } from '@flexo/templates';

import '../assets/colors.scss';
import '../assets/fonts.scss';
import '../assets/typography.scss';
import './app.module.scss';
// eslint-disable-next-line
import '@ionic/react/css/core.css';
import { default as COLORS } from '../assets/colors';

import { useAuth, useClerk, useOrganization, useUser } from '@clerk/clerk-react';
import { LoadingDots } from '@flexo/atoms';
import {
  IconWrapper,
  capturePageleave,
  capturePageview,
  crossDomainConfig,
  getPostAuthRedirectUrl,
  getSavedLang,
  identifyUser,
  isSatellite,
  primarySignInUrl,
  resetPostHog,
  setUserGroup,
  startSessionRecording,
} from '@flexo/general';
import { RoleGuard, EndUserSelfGuard, EndUserSiteGuard, EndUserCommunityGuard, EndUserCommunitiesRedirect, EndUserDataVisRedirect, getOrgRole, isEndUser, isSupervisor, useCalendarParams, useClampCalendarToSitesRange } from '@flexo/hooks';
import { LeftDrawer, RightDrawer } from '@flexo/organisms';
import { CALENDAR, THEME, USER } from '@flexo/reducers';
import { _navigate, setNavigate } from '@flexo/resolvers';
import { useDispatch, useSelector } from 'react-redux';
import { default as MODULES } from './app.modules';


const ENV = (import.meta as unknown as { env: Record<string, string> }).env;

function SatelliteAuthRedirect() {
  useEffect(() => {
    const redirectBack = encodeURIComponent(
      window.location.origin + '/' + ENV.VITE_APP_FIRST_PAGE,
    );
    window.location.href = `${primarySignInUrl}?redirect_url=${redirectBack}`;
  }, []);
  return null;
}

// Registers react-router's navigate with the resolvers' global `_navigate`
// helper as soon as the Router mounts (before any route/resolver renders), so
// `_navigate` is always set. Without this, early `_navigate` calls (e.g. from
// app.tsx's session effect, above the Router) logged "Navigate function not set!".
function NavigationSetter() {
  const navigate = useNavigate();
  setNavigate(navigate);
  return null;
}

/**
 * FLEXO-6064: wildcard catch-all for authenticated users.
 *
 * End-users navigating to a known staff-only route (one with a non-empty
 * `roleGuard` in the module definition) receive /access-denied rather than
 * a silent bounce to /member — making the behaviour consistent with
 * admin-management, api-keys, audit-users etc. which already land on
 * /access-denied via RoleGuard.
 *
 * Admin/supervisor behaviour (and end-user behaviour for truly unknown paths)
 * is unchanged: redirect to the configured first page or /member/:id.
 */
const STAFF_ONLY_PATHS = new Set<string>([
  ...(MODULES as Record<string, unknown>[])
    .filter(
      (m) =>
        Array.isArray((m as any).roleGuard) &&
        (m as any).roleGuard.length > 0,
    )
    .map((m) => String((m as any).name)),
  // FLEXO-6090 #3: known staff-only paths that are not first-class modules here
  // (e.g. the legacy `/users` admin alias of `audit-users`). End-users hitting
  // these get /access-denied, consistent with /admin-management — not a silent
  // bounce to the member home.
  'users',
]);

function WildcardRedirect({ reduxUser }: { reduxUser: any }) {
  const location = useLocation();
  const role = getOrgRole(reduxUser);
  const pathSegment = location.pathname.split('/').filter(Boolean)[0] || '';

  if (isEndUser(role) && STAFF_ONLY_PATHS.has(pathSegment)) {
    return <Navigate to="/access-denied" replace />;
  }

  return (
    <Navigate
      to={
        isEndUser(role) && reduxUser?.metadata?.flexo_user_id
          ? `/member/${reduxUser.metadata.flexo_user_id}`
          : `/${ENV.VITE_APP_FIRST_PAGE}`
      }
      replace
    />
  );
}

/**
 * FLEXO-4604: a supervisor with access to a single community skips the
 * Communities list and lands straight on that community's overview
 * (`/community/:communityId`). Decided only once entities are loaded so the
 * count is reliable; until then the list renders normally (no login delay,
 * no premature redirect).
 */
function SingleCommunityRedirect({ children }: { children: JSX.Element }) {
  const reduxUser = useSelector((state: any) => state.user);
  const metadata = useSelector((state: any) => state.metadata);

  const role = getOrgRole(reduxUser);
  const communities: any[] = metadata?.entities?.community || [];
  const entitiesReady =
    metadata?.EntitiesAsyncStatus === 'SUCCESS' || communities.length > 0;

  if (isSupervisor(role) && entitiesReady && communities.length === 1) {
    const communityId = communities[0]?.communityId;
    if (communityId) {
      return <Navigate to={`/community/${communityId}`} replace />;
    }
  }

  return children;
}

export interface ILoginFlow {
  logInState: 'LOGIN' | 'REGISTER' | 'ACCOUNT_CREATION' | 'FORGOT_PASSWORD';
}

setupIonicReact(); //initiates ionic with react

function App() {
  const dispatch = useDispatch();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const reduxUser = useSelector((state: any) => state.user);
  const calendar = useSelector((state: Record<string, unknown>) => state.calendar);

  // Clerk hooks
  const { user, isLoaded: isUserLoaded } = useUser();
  const { organization } = useOrganization();
  const { signOut } = useAuth();
  const { setActive } = useClerk();
  const lastSessionRef = useRef<{
    userId: string | null;
    orgId: string | null;
  }>({ userId: null, orgId: null });
  // FLEXO-6120/6112: one attempt PER MISMATCH at re-pointing the active org to
  // the tenant org (re-armed whenever the tab is back on the tenant org); if an
  // attempt fails, the normal role check below takes over.
  const orgRepointAttemptedRef = useRef(false);
  const { selectedDate } = calendar.hiveCalendar;
  // Clamp calendar selection to available sites range based on context
  useClampCalendarToSitesRange({ selectedDate, calendarAction: CALENDAR });
  useCalendarParams({ selectedDate, calendarAction: CALENDAR });

  const [showSplashScreen, setShowSplashScreen] = useState(true);
  const [availableModules, setAvailableModules] = useState([]);

  // Remove the HTML-level splash after React has painted its own splash.
  // useLayoutEffect fires after DOM mutations but before browser paint,
  // so the React splash is already in the DOM when we remove the HTML one.
  useLayoutEffect(() => {
    const htmlSplash = document.getElementById('html-splash');
    if (htmlSplash) htmlSplash.remove();
  }, []);

  // On satellite, redirect to primary for login as soon as Clerk confirms
  // there is no active session. This fires from an effect (not from Routes)
  // so the splash can stay visible and cover the redirect — no flash.
  useEffect(() => {
    if (!isSatellite || !isUserLoaded || user) return;
    const redirectBack = encodeURIComponent(
      window.location.origin + '/' + ENV.VITE_APP_FIRST_PAGE,
    );
    console.log(
      '[App] Satellite: no session, redirecting to primary for login',
    );
    window.location.href = `${primarySignInUrl}?redirect_url=${redirectBack}`;
  }, [isUserLoaded, user]);

  const location = window.location;
  const browserLanguage = navigator.language.split('-')[0] || null;

  useEffect(() => {
    (async () => {
      // Check URL param first (for invite/login links with lang)
      const urlParams = new URLSearchParams(window.location.search);
      const urlLang = urlParams.get('lang');

      const savedLang = getSavedLang() || null;

      // Priority: URL param > saved lang > browser lang > default
      let langToUse: string;
      if (urlLang) {
        langToUse = `${ENV.VITE_APP_ORG}_${urlLang}`;
      } else if (savedLang) {
        langToUse = savedLang;
      } else {
        langToUse =
          `${ENV.VITE_APP_ORG}_${browserLanguage}` || ENV.VITE_APP_LANGUAGE;
      }

      await i18next.changeLanguage(langToUse);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- THEME action payload type is not exported
      dispatch(THEME.SetColors({ colors: COLORS } as any));
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect; browserLanguage is a stable primitive from navigator.language
  }, []);

  // DEBUG: Track visibility changes to debug unwanted refreshes
  useEffect(() => {
    const handleVisibility = () => {
      console.log('[App DEBUG] visibilitychange:', document.visibilityState, {
        user: user?.id,
        org: organization?.id,
        isLoaded: isUserLoaded,
      });
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibility);
  }, [user?.id, organization?.id, isUserLoaded]);

  // Handle Clerk user state changes
  useEffect(() => {
    (async () => {
      console.log('[App][session-effect] ENTER', {
        isUserLoaded,
        userId: user?.id ?? null,
        orgId: organization?.id ?? null,
        lastSession: lastSessionRef.current,
        href: window.location.href,
        isSatellite,
        crossDomainConfig,
      });

      if (!isUserLoaded) {
        console.log('[App][session-effect] EXIT: not loaded yet');
        return;
      }

      const currentUserId = user?.id ?? null;
      const currentOrgId = organization?.id ?? null;

      // Prevent false "session changed" during Clerk's window focus revalidation.
      // When the user switches tabs/windows, Clerk temporarily sets user/org to null
      // before restoring the actual session. We ignore this transient null state.
      const wasSignedIn = lastSessionRef.current.userId !== null;
      const isNowTemporarilyNull = currentUserId === null && wasSignedIn;

      if (isNowTemporarilyNull) {
        console.log(
          '[App][session-effect] EXIT: temporary null (revalidation)',
        );
        return;
      }

      const sessionChanged =
        currentUserId !== lastSessionRef.current.userId ||
        currentOrgId !== lastSessionRef.current.orgId;

      if (!sessionChanged) {
        console.log('[App][session-effect] EXIT: no session change');
        return;
      }

      console.log('[App] Session changed detected', {
        from: lastSessionRef.current,
        to: { userId: currentUserId, orgId: currentOrgId },
      });

      if (user && organization) {
        const pathName = location.pathname;
        const pathNameSplit = location.pathname.split('/')?.[1];
        const searchParams = location.search;
        const isForgotPasswordRoute = pathName === '/forgot-password';

        // Cross-domain redirect: check FIRST before showing splash.
        // If we're on primary and need to redirect to satellite, do it immediately
        // without flashing the splash screen.
        // Note: on /login after handleSignIn, both handleSignIn and this effect
        // may call window.location.href with the same satellite URL — the browser
        // deduplicates, so no double navigation occurs.
        const postAuthRedirectUrl = getPostAuthRedirectUrl();
        console.log('[App][session] postAuthRedirectUrl:', postAuthRedirectUrl);
        const isPasswordResetFlow = sessionStorage.getItem('passwordResetInProgress') === 'true';
        if (postAuthRedirectUrl && !isPasswordResetFlow && !isForgotPasswordRoute) {
          console.log(
            '[App][session] >>> REDIRECTING to:',
            postAuthRedirectUrl,
          );
          window.location.href = postAuthRedirectUrl;
          return;
        }

        // FLEXO-6120: the Clerk session's active org is shared across every app
        // on the instance, so multi-org staff can land here with a non-Kwick org
        // active (e.g. AMAG/CKW left active by the VPP apps). The active org is
        // per-tab state: re-point THIS tab to the tenant org instead of signing
        // out — signOut() would kill the shared session for every app in the
        // browser. Users with no tenant-org membership still fall through to the
        // role check below and get signed out there.
        const tenantOrgId = ENV.VITE_APP_CLERK_ORG_ID;
        // FLEXO-6112 #4: the guard must re-arm once the tab is back on the tenant
        // org. The session often BOOTSTRAPS on a foreign org (Clerk seeds the new
        // session with the user's previous last-active org, e.g. CKW from VPP),
        // so the single attempt is consumed during login; a later runtime flip
        // (VPP tab touch) then skipped the re-point and fell through to signOut.
        // One attempt per mismatch, not per page load — still loop-safe: while a
        // mismatch persists the ref stays true.
        if (tenantOrgId && organization.id === tenantOrgId) {
          orgRepointAttemptedRef.current = false;
        }
        if (
          tenantOrgId &&
          organization.id !== tenantOrgId &&
          !isPasswordResetFlow &&
          !isForgotPasswordRoute
        ) {
          const tenantMembership = user.organizationMemberships?.find(
            (m: { organization: { id: string } }) => m.organization.id === tenantOrgId,
          );
          if (tenantMembership && !orgRepointAttemptedRef.current) {
            orgRepointAttemptedRef.current = true;
            console.warn(
              '[App] Active org', organization.id,
              'is not the tenant org, re-pointing to', tenantOrgId,
            );
            try {
              await setActive({ organization: tenantOrgId });
              // Success → this tab is back on the tenant org. Re-arm HERE: the
              // post-re-point effect run exits early at "no session change"
              // (lastSessionRef already holds the tenant org), so the re-arm
              // below this block would never execute after a runtime flip.
              orgRepointAttemptedRef.current = false;
            } catch (error) {
              console.error('[App] Clerk setActive error:', error);
            }
            // organization.id changes → this effect re-runs and bootstraps
            // with the tenant org active.
            return;
          }
        }

        // Raise the splash only when proceeding into a real bootstrap. Raising
        // it before the re-point block left it up forever on a runtime org flip:
        // the post-re-point run exits at "no session change" and nothing ever
        // lowered it (the QA "blank tab"). A runtime re-point now heals silently.
        setShowSplashScreen(true);

        // Extract metadata from Clerk user and organization
        // Get role from organization membership (more reliable than organization.role)
        const orgMembership = user?.organizationMemberships?.find(
          (m: { organization: { id: string }; role?: string }) => m.organization.id === organization.id,
        );
        const orgRole = orgMembership?.role; // "org:admin" | "org:supervisor"
        const orgSlug = organization.slug; // "hive" | "kwick" | "eon" | "plenitude"
        const isAllowedRole =
          orgRole === 'org:admin' ||
          orgRole === 'org:supervisor' ||
          orgRole === 'org:end_user';

        // If memberships haven't loaded yet, wait — don't bootstrap
        if (!orgMembership) {
          console.log('[App] Waiting for org membership data...');
          return;
        }

        lastSessionRef.current = { userId: currentUserId, orgId: currentOrgId };

        // End-user detected — two scenarios:
        // 1. Forgot-password flow in progress: keep session alive (flow needs JWT)
        // 2. Direct login attempt: sign out and show access-denied
        if (!isAllowedRole) {
          const isPasswordResetFlow = sessionStorage.getItem('passwordResetInProgress') === 'true';
          if (isPasswordResetFlow || isForgotPasswordRoute) {
            console.warn('[App] End-user during password reset flow, keeping session alive.');
            setShowSplashScreen(false);
            return;
          }
          console.warn('[App] End-user direct login, signing out.');
          try {
            await signOut();
          } catch (error) {
            console.error('[App] Clerk signOut error:', error);
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- thunk action requires cast due to untyped Redux dispatch
          dispatch(USER.ResetUserAsync() as any);
          resetPostHog();
          window.location.replace('/access-denied');
          return;
        }

        const flexoUserId = user.publicMetadata?.flexo_user_id;
        const flexoOrgId = user.publicMetadata?.flexo_org_id;
        const dbOrgId = user.publicMetadata?.db_org_id;
        const orgFeatures = user.publicMetadata?.org_features;
        const platformSuperadmin = user.publicMetadata?.platform_superadmin;
        const fva = user.publicMetadata?.fva;

        // Build user object compatible with existing Redux structure
        const userData = {
          uid: user.id,
          email: user.primaryEmailAddress?.emailAddress,
          displayName: user.fullName,
          orgRole: orgRole,
          orgSlug: orgSlug,
          metadata: {
            ...user.publicMetadata,
            org_role: orgRole,
            org_slug: orgSlug,
            flexo_user_id: flexoUserId,
            flexo_org_id: flexoOrgId,
            db_org_id: dbOrgId,
            org_features: orgFeatures,
            platform_superadmin: platformSuperadmin,
            fva: fva,
          },
          tokenContent: {
            org_id: organization.id,
            org_role: orgRole,
            org_slug: orgSlug,
            flexo_user_id: flexoUserId,
            flexo_org_id: flexoOrgId,
            db_org_id: dbOrgId,
            org_features: orgFeatures,
            platform_superadmin: platformSuperadmin,
            fva: fva,
          },
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- thunk action requires cast due to untyped Redux dispatch
        dispatch(USER.SetUserAsync(userData) as any);

        // PostHog: Identify user and start session recording
        identifyUser(user.id, {
          email: user.primaryEmailAddress?.emailAddress,
          name: user.fullName,
          org_id: organization.id,
          org_slug: orgSlug,
          org_role: orgRole,
          flexo_user_id: flexoUserId,
          flexo_org_id: flexoOrgId,
          app_source: 'admin-kwick',
          // FLEXO-6062: distinguish the end-user web experience from staff usage.
          app_experience: orgRole === 'org:end_user' ? 'end-user-web' : 'staff-web',
        });
        setUserGroup('organization', organization.id, {
          name: organization.name,
          slug: orgSlug,
        });
        startSessionRecording();

        const moduleNeedsAuth =
          MODULES.find((module: Record<string, unknown>) => module.name === pathNameSplit)
            ?.needsAuth === true;
        console.log(
          '[App][session] pathNameSplit:',
          pathNameSplit,
          'needsAuth:',
          moduleNeedsAuth,
          'href:',
          window.location.href,
        );

        // FLEXO-6112 (#3): routes an end user may legitimately deep-link to.
        // /communities (the staff list) is NOT one of them — it 403s for them.
        const isEndUserOwnRoute =
          pathNameSplit === 'member' ||
          pathNameSplit === 'site' ||
          pathNameSplit === 'community';

        if (
          isEndUser(orgRole) &&
          flexoUserId &&
          !isPasswordResetFlow &&
          !isForgotPasswordRoute
        ) {
          // End users always land on their own member home. Keep a deep link only
          // when it already points at one of their own entity routes; otherwise
          // redirect home instead of leaving them stranded on a staff page such as
          // /communities. Access-denied is reserved for users who genuinely lack
          // access — never for an end user who merely arrived on the wrong landing.
          // memberId comes from authenticated metadata (flexo_user_id), never route input.
          const target = isEndUserOwnRoute
            ? `${pathName}${searchParams}`
            : `/member/${flexoUserId}`;
          console.log('[App][session] >>> _navigate (end-user) to', target);
          setTimeout(() => {
            _navigate(target);
          }, 1000);
        } else if (moduleNeedsAuth) {
          // Staff already on an authenticated route (deep link / refresh): stay there.
          console.log(
            '[App][session] >>> _navigate (needsAuth) to',
            `${pathName}${searchParams}`,
          );
          setTimeout(() => {
            _navigate(`${pathName}${searchParams}`);
          }, 1000);
        }

        setTimeout(() => setShowSplashScreen(false), 2000);
      } else {
        console.log(
          '[App][session-effect] user/org branch: NO user or NO org',
          { user: !!user, org: !!organization },
        );
        lastSessionRef.current = { userId: currentUserId, orgId: currentOrgId };
        setShowSplashScreen(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally tracking by id/memberships only; adding full user/organization objects or location would cause infinite re-renders on Clerk identity changes
  }, [
    user?.id,
    user?.organizationMemberships,
    organization?.id,
    isUserLoaded,
    dispatch,
    signOut,
    setActive,
  ]);

  useEffect(() => {
    let _modules: Record<string, unknown>[] = MODULES.filter(
      (module: Record<string, unknown>) => (module.active as Record<string, unknown>)?.default === true,
    );
    _modules = _modules.filter(
      (module: Record<string, unknown>) =>
        module?.needsAuth === (user ? true : false) ||
        module?.authIndependent === true,
    );
    if (!_.isEqual(_modules, availableModules)) {
      setAvailableModules(_modules);
    }
  }, [user, availableModules]);

  useEffect(() => {
    (async () => {
      if (reduxUser?.logOut === true) {
        console.log('[App] Redux logout detected, processing logout...');

        try {
          // Call Clerk signOut
          console.log('[App] Calling Clerk signOut...');
          await signOut();
          console.log('[App] Clerk signOut successful');
        } catch (error) {
          console.error('[App] Clerk signOut error:', error);
          // Continue with logout even if Clerk fails
        }

        // Handle navigation
        if (localStorage.getItem('redirectToForgotPassword') === 'true') {
          localStorage.removeItem('redirectToForgotPassword');
          console.log('[App] Redirecting to forgot password...');
          window.location.href = '/forgot-password';
        } else {
          console.log('[App] Redirecting to login...');
          _navigate('/login');
        }

        // Reset Redux state
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- thunk action requires cast due to untyped Redux dispatch
        dispatch(USER.ResetUserAsync() as any);

        // PostHog: Stop session recording and reset identity
        resetPostHog();

        // Hide splash screen
        setShowSplashScreen(false);

        console.log('[App] Logout flow complete');
      }
    })();
  }, [reduxUser?.logOut, signOut, dispatch]);

  // Remove the useEffect for dots counter animation as it's now handled by the LoadingDots component
  useEffect(() => {
    if (reduxUser?.splashScreen === true) {
      setShowSplashScreen(true);
    }
  }, [reduxUser?.splashScreen]);

  useEffect(() => {
    // Handle token expiration (401 Unauthorized) events
    const handleTokenExpired = async () => {
      console.log('[App] Token expired event detected');
      try {
        // Log user out if token is expired
        await signOut();
        console.log('[App] Clerk signOut successful after token expiration');
      } catch (error) {
        console.error('[App] Error signing out after token expiration:', error);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- thunk action requires cast due to untyped Redux dispatch
      dispatch(USER.SetUserAsync({ ...reduxUser, logOut: true }) as any);
    };

    // Add event listener for token expiration
    window.addEventListener('clerk-token-expired', handleTokenExpired);

    // Clean up the listener when the component unmounts
    return () => {
      window.removeEventListener('clerk-token-expired', handleTokenExpired);
    };
  }, [reduxUser, dispatch, signOut]);

  // PostHog: Capture pageviews on SPA navigation
  useEffect(() => {
    if (!user) return; // Only track for authenticated users

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      capturePageleave();
      originalPushState.apply(window.history, args);
      capturePageview(window.location.pathname);
    };

    window.history.replaceState = function (...args) {
      originalReplaceState.apply(window.history, args);
      capturePageview(window.location.pathname);
    };

    // Handle browser back/forward buttons
    const handlePopState = () => {
      capturePageview(window.location.pathname);
    };
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', handlePopState);
    };
  }, [user]);

  // Hide splash when Clerk confirms no user — but on satellite keep it visible
  // because SatelliteAuthRedirect will fire a full-page redirect to primary.
  const shouldShowSplash =
    showSplashScreen && !(isUserLoaded && !user && !isSatellite);

  return shouldShowSplash ? (
    <div
      className="flex flex-align-center flex-justify-center flex-column"
      style={{ height: '100vh', width: '100vw', position: 'relative' }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ display: 'flex', lineHeight: 0 }}>
          <IconWrapper
            iconName={ENV?.VITE_APP_IS_WEB === 'true' ? 'logo_wide' : 'logo'}
          />
        </div>
      </div>
      {reduxUser?.isLoggingOut === true && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, 36px)',
          }}
        >
          <LoadingDots />
        </div>
      )}
    </div>
  ) : (
    <IonApp className={`Web__APP`}>
      {
        <IonContent scrollY={false} className="IonScroll">
          <LeftDrawer />
          <BrowserRouter>
            <NavigationSetter />
            {(reduxUser?.SetUserAsyncStatus !== 'PENDING' ||
              (isUserLoaded && !user)) && (
              <Routes>
                {/* Register-by-invite is ALWAYS accessible regardless of auth state */}
                <Route
                  path="/register-by-invite"
                  element={
                    <Modules
                      MODULE={MODULES.find(
                        (m) => m.name === 'register-by-invite',
                      )}
                      COLORS={COLORS}
                    />
                  }
                />

                {/* Login is ALWAYS accessible - handles email param signOut */}
                <Route
                  path="/login"
                  element={
                    <Modules
                      MODULE={MODULES.find((m) => m.name === 'login')}
                      COLORS={COLORS}
                    />
                  }
                />

                {/* Forgot password must stay reachable while signed in: reset links sign out first, then continue here. */}
                <Route
                  path="/forgot-password"
                  element={
                    <Modules
                      MODULE={MODULES.find((m) => m.name === 'forgot-password')}
                      COLORS={COLORS}
                    />
                  }
                />

                <Route path="/access-denied" element={<AccessDenied />} />

                {availableModules.map((module: Record<string, unknown>) => {
                  // No roleGuard on a module => any authenticated user (incl. end-user).
                  // roleGuard is only set on elevated pages (e.g. admin-management).
                  return (
                    <Route
                      key={module.name}
                      path={
                        module?.needsID ? `${module.name}/:id` : module.name
                      }
                      element={
                        <RoleGuard
                          allowed={module?.roleGuard || []}
                          redirectTo="/access-denied"
                        >
                          {module.name === 'member' ? (
                            <EndUserSelfGuard>
                              <Modules MODULE={module} COLORS={COLORS} />
                            </EndUserSelfGuard>
                          ) : module.name === 'site' ? (
                            // FLEXO-6090: block end-users from viewing foreign sites.
                            <EndUserSiteGuard>
                              <Modules MODULE={module} COLORS={COLORS} />
                            </EndUserSiteGuard>
                          ) : module.name === 'community' ? (
                            // FLEXO-6090 / FLEXO-6089 #7: block end-users from
                            // viewing foreign communities.
                            <EndUserCommunityGuard>
                              <Modules MODULE={module} COLORS={COLORS} />
                            </EndUserCommunityGuard>
                          ) : module.name === 'communities' ? (
                            // FLEXO-6133 #1: end-users get their own home
                            // (single site or member aggregate) instead of the
                            // staff communities list. FLEXO-4604: single-
                            // community supervisors skip the list and go
                            // straight to their community overview.
                            <EndUserCommunitiesRedirect>
                              <SingleCommunityRedirect>
                                <Modules MODULE={module} COLORS={COLORS} />
                              </SingleCommunityRedirect>
                            </EndUserCommunitiesRedirect>
                          ) : module.name === 'site-energy' ||
                            module.name === 'site-economics' ||
                            module.name === 'community-energy' ||
                            module.name === 'community-economics' ? (
                            // End users have no separate data-vis pages — these
                            // collapse into /site and /community.
                            <EndUserDataVisRedirect>
                              <Modules MODULE={module} COLORS={COLORS} />
                            </EndUserDataVisRedirect>
                          ) : (
                            <Modules MODULE={module} COLORS={COLORS} />
                          )}
                        </RoleGuard>
                      }
                    />
                  );
                })}

                {user ? (
                  <Route
                    path="*"
                    element={
                      <WildcardRedirect reduxUser={reduxUser} />
                    }
                  />
                ) : isSatellite ? (
                  /* On satellite domain, redirect auth pages to primary domain */
                  <Route path="*" element={<SatelliteAuthRedirect />} />
                ) : (
                  <>
                    {/* All other unauthenticated routes redirect to login */}
                    {/* forgot-password is handled by dynamic module routing via authIndependent flag */}
                    <Route path="*" element={<Navigate to={`/login`} />} />
                  </>
                )}
              </Routes>
            )}
            <RightDrawer />
          </BrowserRouter>
        </IonContent>
      }
    </IonApp>
  );
}

export default App;
